<template>
  <!-- The end of the walkthrough, over the last step: what was seen, the way back into any page of it,
       and the two ways out. Inside the stage, so the timeline under it stays usable — a click there
       leaves this screen as any other move does. -->
  <transition name="vhelp-end">
    <div v-if="endShown" class="vhelp__end">
      <div ref="cardRef" class="vhelp__end-card" role="region" tabindex="-1" :aria-labelledby="titleId">
        <div class="vhelp__end-badge" aria-hidden="true">
          <v-icon size="28">mdi-check</v-icon>
        </div>

        <h2 :id="titleId" class="vhelp__end-title">{{ t("help.end.title", { tour: tourTitle }) }}</h2>
        <p class="vhelp__end-lead" role="status">{{ lead }}</p>

        <div class="vhelp__end-label">{{ t("help.end.revisit") }}</div>
        <div class="vhelp__end-pages">
          <button
            v-for="page in pages"
            :key="page.section"
            type="button"
            class="vhelp__end-page"
            :class="{ 'is-related': page.related }"
            @click="revisit(page.section)"
          >
            <v-icon size="16" class="vhelp__end-page-icon">mdi-play-circle-outline</v-icon>
            <span class="vhelp__end-page-name text-truncate">{{ sectionHeading(page.section) }}</span>
            <span class="vhelp__end-page-count">{{ page.count }}</span>
          </button>
        </div>

        <p class="vhelp__end-hint">{{ t("help.end.hint", { trigger: t("help.trigger.label") }) }}</p>

        <div class="vhelp__end-actions">
          <v-btn variant="text" class="text-none" prepend-icon="mdi-replay" @click="replay">
            {{ t("help.end.replay") }}
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            rounded="lg"
            class="text-none font-weight-bold px-6"
            @click="close('finished')"
          >
            {{ t("help.end.close") }}
          </v-btn>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
/**
 * The walkthrough's end screen. Shown by the player (`endShown`) once the last step has been read or
 * the reader moves on from it; see docs/view-help.md, "The end screen".
 */
import { computed, nextTick, ref, watch } from "vue"
import { useViewHelp } from "@/composables/viewHelp/useViewHelp"
import { useViewHelpCopy } from "@/composables/viewHelp/useViewHelpCopy"

const { endShown, playable, sections, stepsInSection, isRelatedSection, seekSection, setPlaying, togglePlay, close } =
  useViewHelp()
const { t, tourTitle, sectionHeading } = useViewHelpCopy()

const titleId = "vhelp-end-title"
const cardRef = ref<HTMLElement | null>(null)

/** Every section of the tour that had something to show, in playing order, with its number of steps. */
const pages = computed(() =>
  sections.value.map(section => ({
    section,
    related: isRelatedSection(section),
    count: stepsInSection(section).length,
  }))
)

const lead = computed(() => {
  const stepCount = playable.value.length
  const pageCount = pages.value.length
  return t("help.end.lead", {
    steps: t("help.player.steps", { count: stepCount }, stepCount),
    pages: t("help.end.pages", { count: pageCount }, pageCount),
  })
})

/**
 * Back into one page, playing. The end screen stopped playback, and a seek alone leaves it stopped: the
 * walkthrough jumped to the page and stood still on its first step.
 */
function revisit(section: string) {
  setPlaying(true)
  seekSection(section)
}

/** From the top, playing — what the player's own replay button does at the end. */
function replay() {
  togglePlay()
}

// Focus goes to the card, not to a button: Space at the end means "from the top", as on any video
// player, and a focused Schließen would have taken it. The heading is read out, Tab reaches the buttons,
// and Escape still closes, ← still goes back to the last step.
watch(endShown, async shown => {
  if (!shown) return
  await nextTick()
  cardRef.value?.focus()
})
</script>

<style scoped>
/* Over everything the stage draws (camera layers and example pages at 5), level with the loading
   cover, which is never up at the end. */
.vhelp__end {
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);
}

.vhelp__end-card {
  outline: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 460px;
  max-height: 100%;
  overflow-y: auto;
  padding: 24px 24px 16px;
  border-radius: 14px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  text-align: center;
}

.vhelp__end-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 52px;
  height: 52px;
  margin-bottom: 12px;
  border-radius: 50%;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.12);
}

.vhelp__end-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.35;
}

.vhelp__end-lead {
  margin: 4px 0 18px;
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.vhelp__end-label {
  align-self: stretch;
  margin-bottom: 8px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-align: left;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.vhelp__end-pages {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 4px;
}

.vhelp__end-page {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  padding: 7px 10px;
  border: 0;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  color: rgb(var(--v-theme-on-surface));
  font: inherit;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.vhelp__end-page:hover {
  background: rgba(var(--v-theme-primary), 0.1);
}

.vhelp__end-page:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 1px;
}

.vhelp__end-page-icon {
  flex: none;
  color: rgb(var(--v-theme-primary));
}

.vhelp__end-page-name {
  flex: 1 1 auto;
  min-width: 0;
}

.vhelp__end-page-count {
  flex: none;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

/* The related pages carry the timeline's second colour, as their bars do there. */
.vhelp__end-page.is-related .vhelp__end-page-icon {
  color: rgb(var(--v-theme-accent));
}

.vhelp__end-page.is-related:hover {
  background: rgba(var(--v-theme-accent), 0.12);
}

.vhelp__end-hint {
  margin: 14px 0 0;
  font-size: 0.78rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.vhelp__end-actions {
  display: flex;
  flex-wrap: wrap;
  align-self: stretch;
  justify-content: space-between;
  gap: 8px;
  margin-top: 16px;
}

.vhelp-end-enter-active {
  transition: opacity 0.2s ease;
}

.vhelp-end-enter-active .vhelp__end-card {
  transition: transform 0.24s ease;
}

.vhelp-end-leave-active {
  transition: opacity var(--vhelp-leave) ease;
}

.vhelp-end-enter-from,
.vhelp-end-leave-to {
  opacity: 0;
}

.vhelp-end-enter-from .vhelp__end-card {
  transform: translateY(8px) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .vhelp-end-enter-active,
  .vhelp-end-enter-active .vhelp__end-card {
    transition: none;
  }
}
</style>
